import { parseName } from '@nexelem/utils';
import { useFormatTaskDuration, useOnNodeClick } from '../../hooks';
import { NodeIcon } from '../NodeIcon';
import { GanttMode, useProductionPlanQueryParams } from '../../../hooks';
import { MultipleTableAvatar } from '../../../../../molecules/MultipleTableAvatar';
import type { BatchGanttViewBase, TaskGanttView } from '@nexelem/graphne-api';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { Badge, DropdownMenu } from '@nexelem/components';

import clsx from 'clsx';
import { GanttUtils } from '../../../utils';
import { useCustomTranslation } from '../../../../../../hooks';
import { TaskMainValueOptions, useProductionPlanSettings } from '../../../components/SettingsDrawer';
import { formatRequiredWorkers } from '../../../utils/formatRequiredWorkers';
import { NodeFlags } from '../NodeFlags';
import { useNodeMultiplePopperFlagsMapper } from '../../mappers';
import { ProductionOrderNumber } from '../NodeBase';

type NodeMultiplePopperNodeProps = {
  node: TaskGanttView;
  batchNode?: BatchGanttViewBase;
};

export const NodeMultiplePopperNode = ({ batchNode, node }: NodeMultiplePopperNodeProps) => {
  const nodeFlags = useNodeMultiplePopperFlagsMapper(node);

  const { onNodeClick } = useOnNodeClick({ node, batchNode, nodeFlags });

  const { queryParams } = useProductionPlanQueryParams();
  const displayProductName = useProductionPlanSettings((state) => state.taskMainValue === TaskMainValueOptions.PRODUCT);

  const { tWrapper } = useCustomTranslation('products.productionPlan');

  const isWorkerMode = queryParams.mode === GanttMode.WORKER;
  const { productName, name, softResourceAssignments, start, end, requiredSoftResourcesAmount, setupEnd } = node;
  const hasAssignedWorkers = !!softResourceAssignments?.length;

  // For values < 1.0, formatRequiredWorkers returns null, show 1 avatar (worker needed partially)
  // Every task requires at least some worker time, so default to 1 if null/0
  const formattedRequiredWorkers = formatRequiredWorkers(requiredSoftResourcesAmount);
  const requiredWorkersForDisplay = formattedRequiredWorkers ?? 1;

  const avatars = !hasAssignedWorkers
    ? new Array(requiredWorkersForDisplay).fill({})
    : softResourceAssignments?.map(({ name }) => parseName(name));

  const openDetailsDrawer = () => {
    onNodeClick();
    // mode !== 'PUBLICATION' && mode !== 'FREEZING' && onClose();
  };

  return (
    <DropdownMenu.Item
      className={clsx(
        'flex cursor-pointer items-center gap-2 border-border-secondary px-2 not-first:border-t focus:bg-surface-focus-lighter focus-visible:outline-none',
        batchNode ? 'py-2' : 'py-4',
      )}
      onClick={openDetailsDrawer}
    >
      <NodeIcon statusColor={GanttUtils.getTaskStatusColor(node.status, node.errors)} />
      <NodeFlags flags={nodeFlags} nodeId={batchNode?.id || node.id} />
      <div className="flex flex-col items-start">
        <div className="flex items-center gap-2">
          <span className="truncate text-s11-sb">{displayProductName ? productName : name}</span>
          <ProductionOrderNumber
            productionOrderExternalNumber={node.productionOrderDetails?.externalId}
            productionOrderNumber={node.productionOrderDetails?.number}
          />
        </div>
        <NodeProductionTime end={end} setupEnd={setupEnd} start={start} />
        {batchNode && (
          <Badge className="mt-1" StartIcon={<CUSTOM_ICONS.Merge className="text-icon-darker" />}>
            {`${tWrapper('batch')} ${batchNode.name}`}
          </Badge>
        )}
      </div>
      {(isWorkerMode || hasAssignedWorkers) && (
        <MultipleTableAvatar avatars={avatars} className="ml-auto self-center pl-1" />
      )}
    </DropdownMenu.Item>
  );
};

const NodeProductionTime = ({ start, end, setupEnd }: Pick<TaskGanttView, 'start' | 'end' | 'setupEnd'>) => {
  const formatTaskDuration = useFormatTaskDuration({ compact: true });

  if (setupEnd) {
    return (
      <div className="flex flex-col text-s11-sb text-text-secondary-darker">
        <span>{formatTaskDuration({ startDate: new Date(start), endDate: new Date(setupEnd) })}</span>
        <span>{formatTaskDuration({ startDate: new Date(setupEnd), endDate: new Date(end) })}</span>
      </div>
    );
  }

  return (
    <span className="text-s11-sb text-text-secondary-darker">
      {formatTaskDuration({ startDate: new Date(start), endDate: new Date(end) })}
    </span>
  );
};
